/* tslint:disable: no-consecutive-blank-lines prefer-template prefer-for-of prefer-conditional-expression */

import {
  ABC_OFF,
  AbcNote,
  HHAbcSymbol,
  KickAbcSymbol,
  SnareAbcSymbol,
  StickAbcSymbol,
  TomAbcSymbol,
} from "./abc";
import { DrumData } from "./data";
import { range } from "./range";
import { isTripletDivisionFromNotesPerMeasure } from "./utils";

// based on https://raw.githubusercontent.com/montulli/GrooveScribe/afb803a348f18cfca863a6de698a5a5ce809081f/js/groove_utils.js
//
// Author: Lou Montulli
// Original Creation date: Feb 2015.

const debugMode = true;
const grooveDBAuthoring = true;

// global constants
const MAX_MEASURES = 10;
const DEFAULT_TEMPO = 80;

type DrumType = "Stickings" | "H" | "S" | "K" | "B" | "T1" | "T2" | "T3" | "T4";
type TablatureChar =
  | "+"
  | "-"
  | "B"
  | "L"
  | "O"
  | "R"
  | "X"
  | "b"
  | "c"
  | "d"
  | "f"
  | "g"
  | "l"
  | "m"
  | "o"
  | "r"
  | "s"
  | "x";

const defaultDebugMode = true;
const defaultViewMode = true;

// the note grouping size is how groups of notes within a measure group
// for 8ths and 16th we group with 4
// for triplets we group with 3
// This function is for laying out the HTML
// see abc_gen_note_grouping_size for the sheet music layout grouping size
export function noteGroupingSize(
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
): number {
  let note_grouping;
  const usingTriplets = isTripletDivisionFromNotesPerMeasure(
    notes_per_measure,
    timeSigTop,
    timeSigBottom,
  );

  if (usingTriplets) {
    // triplets  ( we only support 2/4 here )
    if (timeSigTop !== 2 && timeSigBottom !== 4) {
      console.warn("Triplets are only supported in 2/4 and 4/4 time");
    }
    note_grouping = notes_per_measure / (timeSigTop * (4 / timeSigBottom));
  } else if (timeSigTop === 3) {
    // 3/4, 3/8, 3/16
    // 3 groups
    // not triplets
    note_grouping = notes_per_measure / 3;
  } else if (timeSigTop % 6 === 0 && timeSigBottom % 8 === 0) {
    // 6/8, 12/8
    // 2 groups in 6/8 rather than 3 groups
    // 4 groups in 12/8
    // not triplets
    note_grouping = notes_per_measure / (2 * timeSigTop / 6);
  } else {
    // figure it out from the time signature
    // not triplets
    note_grouping = notes_per_measure / timeSigTop * (timeSigBottom / 4);
  }
  return note_grouping;
}

// returns an array of [measure, measure], where each measure is
// [notegroup, notegroup, notegroup], and each note group is
// [noteindex, noteindex, noteindex] where note indexes are consecutive
export function createReferenceArray(grooveData: GrooveData | DrumData) {
  const noteGroupSize = noteGroupingSize(
    grooveData.notesPerMeasure,
    grooveData.numBeats,
    grooveData.noteValue,
  );
  const grooveLength = grooveData.notesPerMeasure * grooveData.numberOfMeasures;
  const refArray = range(grooveLength);
  const slicedGroove: Array<Array<Array<number>>> = [];
  for (
    let measureI = 0;
    measureI < grooveLength;
    measureI += grooveData.notesPerMeasure
  ) {
    const measureArray = refArray.slice(
      measureI,
      measureI + grooveData.notesPerMeasure,
    );
    const toPush: Array<Array<number>> = [];
    slicedGroove.push(toPush);
    for (
      let noteGroupI = 0;
      noteGroupI < grooveData.notesPerMeasure;
      noteGroupI += noteGroupSize
    ) {
      toPush.push(measureArray.slice(noteGroupI, noteGroupI + noteGroupSize));
    }
  }
  return slicedGroove;
}

export class GrooveData {
  timeDivision = 16;
  numBeats = 4; // TimeSigTop: Top part of Time Signature 3/4, 4/4, 5/4, 6/8, etc...
  noteValue = 4; // TimeSigBottom: Bottom part of Time Signature   4 = quarter notes, 8 = 8th notes, 16ths, etc..
  numberOfMeasures = 1;
  get notesPerMeasure() {
    return this.timeDivision / this.noteValue * this.numBeats;
  }
  sticking_array: Array<AbcNote> = newEmptyNoteArray(
    this.timeDivision,
    this.numberOfMeasures,
  );
  hh_array: Array<AbcNote> = newEmptyNoteArray(
    this.timeDivision,
    this.numberOfMeasures,
  );
  snare_array: Array<AbcNote> = newEmptyNoteArray(
    this.timeDivision,
    this.numberOfMeasures,
  );
  kick_array: Array<AbcNote> = newEmptyNoteArray(
    this.timeDivision,
    this.numberOfMeasures,
  );
  // toms_array contains 4 toms  T1, T2, T3, T4 index starting at zero
  toms_array: Array<Array<AbcNote>> = [
    newEmptyNoteArray(this.timeDivision, this.numberOfMeasures),
    newEmptyNoteArray(this.timeDivision, this.numberOfMeasures),
    newEmptyNoteArray(this.timeDivision, this.numberOfMeasures),
    newEmptyNoteArray(this.timeDivision, this.numberOfMeasures),
  ];
  showToms = false;
  showStickings = false;
  title = "";
  author = "";
  comments = "";
  showLegend = false;
  swingPercent = 0;
  tempo = DEFAULT_TEMPO;
  kickStemsUp = true;
  metronomeFrequency = 0; // 0, 4, 8, 16
  grooveDBAuthoring = grooveDBAuthoring;
  debugMode = defaultDebugMode;
  viewMode = defaultViewMode;
}

function getQueryVariableFromString(
  variable: string,
  def_value: string | number | boolean,
  my_string: string,
): string {
  const query = my_string.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=");
    if (pair[0].toLowerCase() === variable.toLowerCase()) {
      return pair[1];
    }
  }
  return def_value as string;
}

function newEmptyNoteArray(
  notes_per_measure: number,
  numMeasures: number,
): Array<typeof ABC_OFF> {
  return new Array(notes_per_measure * numMeasures).fill(ABC_OFF);
}

export function newDefaultStickingsArray(
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
  numMeasures: number,
): Array<StickAbcSymbol | typeof ABC_OFF> {
  return newEmptyNoteArray(notes_per_measure, numMeasures).fill(
    StickAbcSymbol.COUNT,
  );
}

export function newDefaultHHArray(
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
  numMeasures: number,
): Array<HHAbcSymbol | typeof ABC_OFF> {
  return new Array(numMeasures * notes_per_measure).fill(HHAbcSymbol.Normal);
}

// |--------O---------------O-------|
export function newDefaultSnareArray(
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
  numMeasures: number,
): Array<SnareAbcSymbol | typeof ABC_OFF> {
  const measureArray = [];
  const notes_per_grouping = notes_per_measure / timeSigTop;

  for (let i = 0; i < notes_per_measure; i++) {
    // if the note falls on the beginning of a group
    // and the group is odd
    if (i % notes_per_grouping === 0 && (i / notes_per_grouping) % 2 !== 0) {
      measureArray.push(SnareAbcSymbol.Normal);
    } else {
      measureArray.push(ABC_OFF);
    }
  }

  return [].concat(...new Array(numMeasures).fill(measureArray));
}

// |o---------------o---------------|
export function newDefaultKickArray(
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
  numMeasures: number,
): Array<KickAbcSymbol | typeof ABC_OFF> {
  const measureArray = [];
  const notes_per_grouping = notes_per_measure / timeSigTop;

  for (let i = 0; i < notes_per_measure; i++) {
    // if the note falls on the beginning of a group
    // and the group is even
    if (i % notes_per_grouping === 0 && (i / notes_per_grouping) % 2 === 0) {
      measureArray.push(KickAbcSymbol.Normal);
    } else {
      measureArray.push(ABC_OFF);
    }
  }

  return [].concat(...new Array(numMeasures).fill(measureArray));
}

export function newDefaultTomArray(
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
  numMeasures: number,
): Array<TomAbcSymbol | typeof ABC_OFF> {
  return newEmptyNoteArray(notes_per_measure, numMeasures);
}

// takes a character from tablature form and converts it to our ABC Notation form.
// uses drum tab format adapted from wikipedia: http://en.wikipedia.org/wiki/Drum_tablature
//
//  Sticking support:
//		R: right
//      L: left
//
//  HiHat support:
//		x: normal
//		X: accent
//		o: open
//		+: close
//		c: crash
//		r: ride
//		b: ride bell
//		m: (more) cow bell
//      s: stacker
//		-: off
//
//   Snare support:
//		o: normal
//		O: accent
//		g: ghost
//		x: cross stick
//		f: flam
//		-: off
//
//   Kick support:
//		o: normal
//		x: hi hat splash with foot
//		X: kick & hi hat splash with foot simultaneously
//
//  Kick can be notated either with a "K" or a "B"
//
//  Note that "|" and " " will be skipped so that standard drum tabs can be applied
//  Example:
//     H=|x---x---x---x---|x---x---x---x---|x---x---x---x---|
// or  H=x-x-x-x-x-x-x-x-x-x-x-x-
//     S=|----o-------o---|----o-------o---|----o-------o---|
// or  S=--o---o---o---o---o---o-
//     B=|o-------o-------|o-------o-o-----|o-----o-o-------|
// or  K=o---o---o----oo-o--oo---|
// or  T1=|o---o---o---o|
// or  T2=|o---o---o---o|
// or  T3=|o---o---o---o|
// or  T4=|o---o---o---o|
function tablatureToABCNotationPerNote(
  drumType: DrumType,
  tablatureChar: TablatureChar,
): AbcNote {
  switch (tablatureChar) {
    case "b":
    case "B":
      if (drumType === "Stickings") {
        return StickAbcSymbol.BOTH;
      } else if (drumType === "H") {
        return HHAbcSymbol.Ride_Bell;
      } else if (drumType === "S") {
        return SnareAbcSymbol.Buzz;
      }
      break;
    case "c":
      if (drumType === "Stickings") {
        return StickAbcSymbol.COUNT;
      } else if (drumType === "H") {
        return HHAbcSymbol.Crash;
      }
      break;
    case "d":
      if (drumType === "S") {
        return SnareAbcSymbol.Drag;
      }
      break;
    case "f":
      if (drumType === "S") {
        return SnareAbcSymbol.Flam;
      }
      break;
    case "g":
      if (drumType === "S") {
        return SnareAbcSymbol.Ghost;
      }
      break;
    case "l":
    case "L":
      if (drumType === "Stickings") {
        return StickAbcSymbol.L;
      }
      break;
    case "m": // (more) cow bell
      if (drumType === "H") {
        return HHAbcSymbol.Cow_Bell;
      }
      break;
    case "O":
      if (drumType === "S") {
        return SnareAbcSymbol.Accent;
      }
      break;
    case "o":
      switch (drumType) {
        case "H":
          return HHAbcSymbol.Open;
        //break;
        case "S":
          return SnareAbcSymbol.Normal;
        //break;
        case "K":
        case "B":
          return KickAbcSymbol.Normal;
        //break;
        case "T1":
          return TomAbcSymbol.T1;
        //break;
        case "T2":
          return TomAbcSymbol.T2;
        //break;
        case "T3":
          return TomAbcSymbol.T3;
        //break;
        case "T4":
          return TomAbcSymbol.T4;
        //break;
        default:
          break;
      }
      break;
    case "r":
    case "R":
      switch (drumType) {
        case "H":
          return HHAbcSymbol.Ride;
        //break;
        case "Stickings":
          return StickAbcSymbol.R;
        //break;
        default:
          break;
      }
      break;
    case "s":
      if (drumType === "H") {
        return HHAbcSymbol.Stacker;
      }
      break;
    case "x":
      switch (drumType) {
        case "S":
          return SnareAbcSymbol.XStick;
        //break;
        case "K":
        case "B":
          return KickAbcSymbol.Splash;
        //break;
        case "H":
          return HHAbcSymbol.Normal;
        //break;
        case "T1":
          return TomAbcSymbol.T1;
        //break;
        case "T4":
          return TomAbcSymbol.T4;
        //break;
        default:
          break;
      }
      break;
    case "X":
      switch (drumType) {
        case "K":
          return KickAbcSymbol.SandK;
        //break;
        case "H":
          return HHAbcSymbol.Accent;
        //break;
        default:
          break;
      }
      break;
    case "+":
      if (drumType === "H") {
        return HHAbcSymbol.Close;
      }
      break;
    case "-":
      return ABC_OFF;
    //break;
    default:
      break;
  }

  console.warn(
    "Bad tablature note found in tablatureToABCNotationPerNote.  Tab: " +
      tablatureChar +
      " for drum type: " +
      drumType,
  );
  return ABC_OFF;
}

// takes a string of notes encoded in a serialized string and convert it to an array that represents the notes
// uses drum tab format adapted from wikipedia: http://en.wikipedia.org/wiki/Drum_tablature
//
//  Note that "|" and " " will be skipped so that standard drum tabs can be applied
//  Example:
//     H=|x---x---x---x---|x---x---x---x---|x---x---x---x---|
// or  H=x-x-x-x-x-x-x-x-x-x-x-x-
//     S=|----o-------o---|----o-------o---|----o-------o---|
// or  S=--o---o---o---o---o---o-
//     B=|o-------o-------|o-------o-o-----|o-----o-o-------|
// or  B=o---o---o----oo-o--oo---|
//
// Returns array that contains notesPerMeasure * numberOfMeasures entries.
function noteArraysFromURLData(
  drumType: DrumType,
  noteString: string,
  notesPerMeasure: number,
  numberOfMeasures: number,
): Array<AbcNote> {
  const retArray: Array<AbcNote> = [];

  // decode the %7C url encoding types
  noteString = decodeURIComponent(noteString);

  const retArraySize = notesPerMeasure * numberOfMeasures;

  // ignore "|" by removing them
  //var notes = noteString.replace(/\|/g, '');
  // ignore "|" & ")" & "(" & "[" & "]" & "!" & ":" by removing them
  const notes = noteString.replace(/\:|\!|\)|\(|\[|\]|\|/g, "");

  let noteStringScaler = 1;
  let displayScaler = 1;
  if (notes.length > retArraySize && notes.length / retArraySize >= 2) {
    // if we encounter a 16th note groove for an 8th note board, let's scale it	down
    noteStringScaler = Math.ceil(notes.length / retArraySize);
  } else if (notes.length < retArraySize && retArraySize / notes.length >= 2) {
    // if we encounter a 8th note groove for an 16th note board, let's scale it up
    displayScaler = Math.ceil(retArraySize / notes.length);
  }

  // initialize an array that can carry all the measures in one array
  for (let i = 0; i < retArraySize; i++) {
    retArray[i] = ABC_OFF;
  }

  let retArrayIndex = 0;
  for (
    let j = 0;
    j < notes.length && retArrayIndex < retArraySize;
    j += noteStringScaler, retArrayIndex += displayScaler
  ) {
    retArray[retArrayIndex] = tablatureToABCNotationPerNote(drumType, notes[
      j
    ] as TablatureChar);
  }

  return retArray;
}

// parse a string like "4/4", "5/4" or "2/4"
function parseTimeSigString(timeSigString: string): [number, number] {
  const split_arr = timeSigString.split("/");

  if (split_arr.length !== 2) {
    return [4, 4];
  }

  let timeSigTop = parseInt(split_arr[0], 10);
  let timeSigBottom = parseInt(split_arr[1], 10);

  if (timeSigTop < 1 || timeSigTop > 32) {
    timeSigTop = 4;
  }

  // only valid if 2,4,8, or 16
  if (
    timeSigBottom !== 2 &&
    timeSigBottom !== 4 &&
    timeSigBottom !== 8 &&
    timeSigBottom !== 16
  ) {
    timeSigBottom = 4;
  }

  return [timeSigTop, timeSigBottom];
}

export function getGrooveDataFromUrlString(encodedURLData: string) {
  const myGrooveData = new GrooveData();

  myGrooveData.debugMode = !!parseInt(
    getQueryVariableFromString("Debug", debugMode, encodedURLData),
    10,
  );

  const timeSigArray = parseTimeSigString(
    getQueryVariableFromString("TimeSig", "4/4", encodedURLData),
  );
  myGrooveData.numBeats = timeSigArray[0];
  myGrooveData.noteValue = timeSigArray[1];

  myGrooveData.timeDivision = parseInt(
    getQueryVariableFromString("Div", 16, encodedURLData),
    10,
  );

  myGrooveData.metronomeFrequency = parseInt(
    getQueryVariableFromString("MetronomeFreq", "0", encodedURLData),
    10,
  );

  myGrooveData.numberOfMeasures = parseInt(
    getQueryVariableFromString("measures", 1, encodedURLData),
    10,
  );
  if (
    myGrooveData.numberOfMeasures < 1 ||
    isNaN(myGrooveData.numberOfMeasures)
  ) {
    myGrooveData.numberOfMeasures = 1;
  } else if (myGrooveData.numberOfMeasures > MAX_MEASURES) {
    myGrooveData.numberOfMeasures = MAX_MEASURES;
  }

  const Stickings_string = getQueryVariableFromString(
    "Stickings",
    false,
    encodedURLData,
  );
  if (Stickings_string) {
    myGrooveData.sticking_array = noteArraysFromURLData(
      "Stickings",
      Stickings_string,
      myGrooveData.notesPerMeasure,
      myGrooveData.numberOfMeasures,
    );
    myGrooveData.showStickings = true;
  } else {
    myGrooveData.sticking_array = newDefaultStickingsArray(
      myGrooveData.notesPerMeasure,
      myGrooveData.numBeats,
      myGrooveData.noteValue,
      myGrooveData.numberOfMeasures,
    );
    myGrooveData.showStickings = false;
  }

  const HH_string = getQueryVariableFromString("H", false, encodedURLData);
  if (HH_string) {
    myGrooveData.hh_array = noteArraysFromURLData(
      "H",
      HH_string,
      myGrooveData.notesPerMeasure,
      myGrooveData.numberOfMeasures,
    );
  } else {
    myGrooveData.hh_array = newDefaultHHArray(
      myGrooveData.notesPerMeasure,
      myGrooveData.numBeats,
      myGrooveData.noteValue,
      myGrooveData.numberOfMeasures,
    );
  }

  const Snare_string = getQueryVariableFromString("S", false, encodedURLData);
  if (Snare_string) {
    myGrooveData.snare_array = noteArraysFromURLData(
      "S",
      Snare_string,
      myGrooveData.notesPerMeasure,
      myGrooveData.numberOfMeasures,
    );
  } else {
    myGrooveData.snare_array = newDefaultSnareArray(
      myGrooveData.notesPerMeasure,
      myGrooveData.numBeats,
      myGrooveData.noteValue,
      myGrooveData.numberOfMeasures,
    );
  }

  const Kick_string = getQueryVariableFromString("K", false, encodedURLData);
  if (Kick_string) {
    myGrooveData.kick_array = noteArraysFromURLData(
      "K",
      Kick_string,
      myGrooveData.notesPerMeasure,
      myGrooveData.numberOfMeasures,
    );
  } else {
    myGrooveData.kick_array = newDefaultKickArray(
      myGrooveData.notesPerMeasure,
      myGrooveData.numBeats,
      myGrooveData.noteValue,
      myGrooveData.numberOfMeasures,
    );
  }

  // Get the Toms
  for (let i = 0; i < 4; i++) {
    // toms are named T1, T2, T3, T4
    const Tom_string = getQueryVariableFromString(
      "T" + (i + 1),
      false,
      encodedURLData,
    );
    if (Tom_string) {
      myGrooveData.toms_array[i] = noteArraysFromURLData(
        `T${i + 1}` as DrumType,
        Tom_string,
        myGrooveData.notesPerMeasure,
        myGrooveData.numberOfMeasures,
      );
      myGrooveData.showToms = true;
    } else {
      myGrooveData.toms_array[i] = newDefaultTomArray(
        myGrooveData.notesPerMeasure,
        myGrooveData.numBeats,
        myGrooveData.noteValue,
        myGrooveData.numberOfMeasures,
      );
    }
  }

  myGrooveData.title = getQueryVariableFromString("title", "", encodedURLData);
  myGrooveData.title = decodeURIComponent(myGrooveData.title);
  myGrooveData.title = myGrooveData.title.replace(/\+/g, " ");

  myGrooveData.author = getQueryVariableFromString(
    "author",
    "",
    encodedURLData,
  );
  myGrooveData.author = decodeURIComponent(myGrooveData.author);
  myGrooveData.author = myGrooveData.author.replace(/\+/g, " ");

  myGrooveData.comments = getQueryVariableFromString(
    "comments",
    "",
    encodedURLData,
  );
  myGrooveData.comments = decodeURIComponent(myGrooveData.comments);
  myGrooveData.comments = myGrooveData.comments.replace(/\+/g, " ");

  myGrooveData.tempo = parseInt(
    getQueryVariableFromString("tempo", DEFAULT_TEMPO, encodedURLData),
    10,
  );
  if (
    isNaN(myGrooveData.tempo) ||
    myGrooveData.tempo < 20 ||
    myGrooveData.tempo > 400
  ) {
    myGrooveData.tempo = DEFAULT_TEMPO;
  }

  myGrooveData.swingPercent = parseInt(
    getQueryVariableFromString("swing", 0, encodedURLData),
    10,
  );
  if (
    isNaN(myGrooveData.swingPercent) ||
    myGrooveData.swingPercent < 0 ||
    myGrooveData.swingPercent > 100
  ) {
    myGrooveData.swingPercent = 0;
  }

  return myGrooveData;
}



// WEBPACK FOOTER //
// ./src/grooveUtils.ts